import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

const datasetKey = (profile) => `${profile.fileName}\u0000${profile.sheetName}`;
const positivePattern = /paid|repaid|performing|current|good|success|funded|approved|complete|^yes$|^true$|^1$/i;
const negativePattern = /default|delinquent|failed|loss|write.?off|written.?off|declined|rejected|^no$|^false$|^0$/i;

export function suggestLabelValueMapping(values = []) {
  const positiveValues = values.filter((item) => positivePattern.test(item.value)).map((item) => item.value);
  const negativeValues = values.filter((item) => negativePattern.test(item.value)).map((item) => item.value)
    .filter((value) => !positiveValues.includes(value));
  return { positiveValues, negativeValues };
}

export default function PaymentPlatformLabelDialog({
  open,
  preview,
  busy = false,
  onCancel,
  onContinueWithoutLabel,
  onConfirm,
}) {
  const profiles = useMemo(() => preview?.profiles || [], [preview]);
  const defaultProfile = useMemo(
    () => profiles.find((profile) => profile.columns?.some((column) => column.recommended)) || profiles[0] || null,
    [profiles],
  );
  const [dataset, setDataset] = useState("");
  const [columnName, setColumnName] = useState("");
  const [positiveValues, setPositiveValues] = useState([]);
  const [negativeValues, setNegativeValues] = useState([]);

  const profile = profiles.find((item) => datasetKey(item) === dataset) || defaultProfile;
  const columns = profile?.columns || [];
  const column = columns.find((item) => item.name === columnName) || null;
  const values = column?.values || [];

  useEffect(() => {
    if (!open || !defaultProfile) return;
    const initialColumn = defaultProfile.columns.find((item) => item.recommended) || defaultProfile.columns[0];
    setDataset(datasetKey(defaultProfile));
    setColumnName(initialColumn?.name || "");
  }, [open, defaultProfile]);

  useEffect(() => {
    if (!column) {
      setPositiveValues([]);
      setNegativeValues([]);
      return;
    }
    const suggestion = suggestLabelValueMapping(values);
    setPositiveValues(suggestion.positiveValues);
    setNegativeValues(suggestion.negativeValues);
  }, [columnName, dataset]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseDataset = (value) => {
    const nextProfile = profiles.find((item) => datasetKey(item) === value);
    const nextColumn = nextProfile?.columns?.find((item) => item.recommended) || nextProfile?.columns?.[0];
    setDataset(value);
    setColumnName(nextColumn?.name || "");
  };

  const mappedPositive = values
    .filter((item) => positiveValues.includes(item.value))
    .reduce((sum, item) => sum + item.count, 0);
  const mappedNegative = values
    .filter((item) => negativeValues.includes(item.value))
    .reduce((sum, item) => sum + item.count, 0);
  const canConfirm = profile && column && positiveValues.length > 0 && negativeValues.length > 0;

  const confirm = () => onConfirm({
    fileName: profile.fileName,
    sheetName: profile.sheetName,
    column: column.name,
    positiveValues,
    negativeValues,
  });

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>Choose the model outcome label</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ color: "#64748b", mb: 2 }}>
          Select the uploaded column that tells CrediArc what ultimately happened. This label is used for
          model training and validation; it does not expose the source filename to client users.
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="label-dataset">File and sheet</InputLabel>
            <Select
              labelId="label-dataset"
              label="File and sheet"
              value={profile ? datasetKey(profile) : ""}
              onChange={(event) => chooseDataset(event.target.value)}
            >
              {profiles.map((item) => (
                <MenuItem key={datasetKey(item)} value={datasetKey(item)}>
                  {item.fileName} · {item.sheetName} ({Number(item.rowCount || 0).toLocaleString()} rows)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="label-column">Outcome column</InputLabel>
            <Select
              labelId="label-column"
              label="Outcome column"
              value={columnName}
              onChange={(event) => setColumnName(event.target.value)}
            >
              {columns.map((item) => (
                <MenuItem key={item.name} value={item.name}>
                  <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 1 }}>
                    <span>{item.name}</span>
                    <span style={{ color: "#64748b" }}>
                      {item.distinctCount} values · {item.coverage}% populated
                    </span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!column}>
            <InputLabel id="positive-values">Positive / performing values</InputLabel>
            <Select
              multiple
              labelId="positive-values"
              label="Positive / performing values"
              value={positiveValues}
              onChange={(event) => {
                const next = event.target.value;
                setPositiveValues(next);
                setNegativeValues((current) => current.filter((value) => !next.includes(value)));
              }}
              renderValue={(selected) => selected.join(", ")}
            >
              {values.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={positiveValues.includes(item.value)} />
                  <ListItemText primary={item.value} secondary={`${item.count.toLocaleString()} rows`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!column}>
            <InputLabel id="negative-values">Negative / non-performing values</InputLabel>
            <Select
              multiple
              labelId="negative-values"
              label="Negative / non-performing values"
              value={negativeValues}
              onChange={(event) => {
                const next = event.target.value;
                setNegativeValues(next);
                setPositiveValues((current) => current.filter((value) => !next.includes(value)));
              }}
              renderValue={(selected) => selected.join(", ")}
            >
              {values.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={negativeValues.includes(item.value)} />
                  <ListItemText primary={item.value} secondary={`${item.count.toLocaleString()} rows`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {column && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
            <Chip color="success" label={`${mappedPositive.toLocaleString()} positive rows`} />
            <Chip color="error" label={`${mappedNegative.toLocaleString()} negative rows`} />
            <Chip label={`${Math.max(0, Number(profile?.rowCount || 0) - mappedPositive - mappedNegative).toLocaleString()} ignored rows`} />
          </Box>
        )}

        {column?.valuesTruncated && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This column has more than 50 distinct values. Only its most frequent values are shown; a lower-cardinality
            outcome column is safer for model training.
          </Alert>
        )}
        {column && column.distinctCount < 2 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This column contains only one observed value and cannot provide both positive and negative training outcomes.
          </Alert>
        )}
        {!canConfirm && column && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Choose at least one positive and one negative value. Rows with other values will be ignored for supervised ML.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={onContinueWithoutLabel} disabled={busy}>Continue without a label</Button>
        <Button variant="contained" onClick={confirm} disabled={busy || !canConfirm}>
          {busy ? "Starting import…" : "Use label and start import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
